/*
 * cobranzaLogica.js — Lógica pura de la pantalla de Cobranza (C-017).
 *
 * El reparto de un depósito es el mismo que hace el servidor (backend/cobranza.js): a los meses
 * más antiguos primero; lo que sobra queda como saldo a favor. Aquí solo se usa para mostrar
 * "Se aplica a" antes de confirmar. Todo en centavos para que las sumas cuadren.
 */

export const centavos = (n) => Math.round(Number(n || 0) * 100);
export const pesos = (c) => Math.round(c) / 100;

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
export const nombreMes = (mes) => MESES[mes - 1] || '';
export const nombreMesAnio = (mes, anio) => `${nombreMes(mes)} ${anio}`;

export const formatoPesos = (n) =>
  `$${Number(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const claveMes = (mes, anio) => Number(anio) * 12 + Number(mes) - 1;

/** true si el mes (1-12) ya pasó respecto a hoy. */
export const mesYaPaso = (mes, anio, hoy = new Date()) =>
  claveMes(mes, anio) < claveMes(hoy.getMonth() + 1, hoy.getFullYear());

/**
 * Reparte un depósito entre los meses pendientes.
 * pendientes: [{ cobro_id, mes, anio, por_cobrar }] en pesos.
 * Devuelve { aplicaciones: [{ ...mes, aplicado, falta, completo }], siguen: [...], sobra } en pesos.
 */
export function repartir(pendientes, monto) {
  let resto = centavos(monto);
  const aplicaciones = [];
  const siguen = [];
  const ordenados = [...(pendientes || [])]
    .filter(p => centavos(p.por_cobrar) > 0)
    .sort((a, b) => claveMes(a.mes, a.anio) - claveMes(b.mes, b.anio));
  for (const p of ordenados) {
    const debe = centavos(p.por_cobrar);
    if (resto <= 0) {
      siguen.push(p);
      continue;
    }
    const aplicado = Math.min(resto, debe);
    aplicaciones.push({ ...p, aplicado: pesos(aplicado), falta: pesos(debe - aplicado), completo: aplicado === debe });
    resto -= aplicado;
  }
  return { aplicaciones, siguen, sobra: pesos(Math.max(resto, 0)) };
}

/** "$4,060.65", "4060.65" o "4,060" → 4060.65; null si no es un monto. */
export function parsearMonto(texto) {
  const limpio = String(texto || '').trim().replace(/[$\s]/g, '').replace(/,/g, '');
  if (!/^\d+(\.\d{1,2})?$/.test(limpio)) return null;
  const n = Number(limpio);
  return n > 0 ? n : null;
}

/**
 * Si un cliente "debe exactamente" un monto: el por cobrar del mes elegido, su saldo total o la
 * suma de sus meses más antiguos (1, 2, 3… meses). Tolerancia de un centavo.
 */
export function coincideMonto(cliente, monto) {
  const objetivo = centavos(monto);
  const cerca = (c) => Math.abs(c - objetivo) <= 1;
  if (cliente.cobro && cerca(centavos(cliente.cobro.por_cobrar))) return true;
  let acumulado = 0;
  for (const p of cliente.pendientes || []) {
    acumulado += centavos(p.por_cobrar);
    if (cerca(acumulado)) return true;
  }
  return false;
}

/** Total que debe un cliente (todos sus meses pendientes). */
export const totalPendiente = (cliente) =>
  pesos((cliente.pendientes || []).reduce((s, p) => s + centavos(p.por_cobrar), 0));
